import React, {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
} from 'react';
import PropTypes from 'prop-types';

import BackDrop from '../BackDrop';
import Portal from '../Portal';
import DrawerContainer from './DrawerContainer';
import { useDrawer } from './useDrawer';

const AnimatedDrawerContainer = forwardRef(
  ({ open, size, fullHeight, onClick, children }, ref) => {
    const { state, closed, transitionDuration } = useDrawer({
      isOpen: open,
      transitionDuration: 400,
    });
    return closed ? null : (
      <DrawerContainer
        size={size}
        state={state}
        transitionDuration={`${transitionDuration}ms`}
        fullHeight={fullHeight}
        onClick={onClick}
        ref={ref}
      >
        {children}
      </DrawerContainer>
    );
  }
);

const stopPropagation = (e) => e.stopPropagation();

const Drawer = forwardRef(
  (
    {
      children,
      rootElement,
      open = false,
      size = DEFAULT_SIZE,
      footer,
      onDismiss = Function.prototype,
      backDrop = <BackDrop onClick={onDismiss} open={open} />,
    },
    ref
  ) => {
    return (
      <Portal rootElement={rootElement}>
        {backDrop}
        <AnimatedDrawerContainer
          size={size}
          open={open}
          onClick={stopPropagation}
          ref={ref}
        >
          {children &&
            Children.map(children, (child) =>
              isValidElement(child) ? cloneElement(child) : null
            )}
          {footer}
        </AnimatedDrawerContainer>
      </Portal>
    );
  }
);

const sizes = {
  full: 'full',
  large: 'large',
};
const DEFAULT_SIZE = sizes.large;

Drawer.sizes = sizes;
Drawer.defaultSize = DEFAULT_SIZE;

Drawer.propTypes = {
  /** Whether the drawer is open. Is passed to the DrawerWrapper. */
  open: PropTypes.bool,
  /** The size (width) of the DrawerWrapper. Full is 100% width. */
  size: PropTypes.oneOf(Object.keys(sizes)),
  /** The drawer footer component. Normally a DrawerFooter. */
  footer: PropTypes.node,
  /** Whether the header and footer elements should be fixed, and only the DrawerContent scrollable. This property is passed to the DrawerContainer and the children (DrawerContent) */
  fixed: PropTypes.bool,
};

export default Drawer;
